import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { LandingNav } from "@/components/landing/LandingNav";
import { Hero } from "@/components/landing/Hero";
import { HowItWorks } from "@/components/landing/HowItWorks";
import { Offer } from "@/components/landing/Offer";
import { GovernanceFeatures } from "@/components/landing/GovernanceFeatures";
import { MarketplacePreview } from "@/components/landing/MarketplacePreview";
import { GuaranteeBand } from "@/components/landing/GuaranteeBand";
import { FAQ } from "@/components/landing/FAQ";
import { FinalCTA } from "@/components/landing/FinalCTA";
import { LandingFooter } from "@/components/landing/LandingFooter";

// The free public gallery is top-of-funnel proof, not the headline — kept as real, live data.
function useGalleryPreview() {
  const [skills, setSkills] = useState(null); // null=loading, []=empty
  const [stats, setStats] = useState({ count: 0, avg: null });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/marketplace?sort=top_rated");
        setSkills(data);
        const rated = data.filter((s) => s.review_count > 0);
        const avg = rated.length ? (rated.reduce((a, s) => a + (s.avg_rating || 0), 0) / rated.length) : null;
        setStats({ count: data.length, avg });
      } catch (e) {
        console.error("[landing] failed to load gallery preview", e);
        setSkills([]);
      }
    })();
  }, []);

  return { skills, stats };
}

export default function Landing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { skills, stats } = useGalleryPreview();

  // Logged-out CTAs land on /signup (choice of Google or email) — never straight into the
  // Google OAuth redirect (2026-07-14). The redirect param carries the original intent.
  const startTeam = () => navigate(user ? "/orgs/new" : "/signup?redirect=/orgs/new");
  const start = () => navigate(user ? "/build" : "/signup?redirect=/build");
  const openGallery = () => navigate("/marketplace"); // public route — no login gate for the funnel
  const goLogin = () => navigate("/login");

  return (
    <div className="min-h-screen bg-[color:var(--rb-void)]">
      <LandingNav user={user} login={goLogin} startTeam={startTeam} navigate={navigate} />

      <main id="top">
        <Hero startTeam={startTeam} stats={stats} />
        <HowItWorks />
        <Offer startTeam={startTeam} />
        <GovernanceFeatures />
        <MarketplacePreview skills={skills} start={start} openGallery={openGallery} startTeam={startTeam} />
        <GuaranteeBand startTeam={startTeam} />
        <FAQ />
        <FinalCTA startTeam={startTeam} openGallery={openGallery} />
      </main>

      <LandingFooter login={goLogin} />
    </div>
  );
}
